import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { TypedMemo } from '../../../../common/types';
import { isEmailOnlyService } from './presaveSubscribeServices';
import { presaveSubscribeMessages } from './presaveSubscribeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole card; each part uses its own slice of it */
import styles from './PresaveSubscribeWidget.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveSubscribeServiceRowProps {
  /** A `MusicAPIIntegrationID` value. */
  integrationType: string;
  checked: boolean;
  disabled: boolean;
  onToggle: (integrationType: string) => void;
}

/** One service of the "Get every release" block: a tick, the brand dot and the name (design d6). */
const PresaveSubscribeServiceRowComponent: React.FC<PresaveSubscribeServiceRowProps> = ({
  integrationType,
  checked,
  disabled,
  onToggle,
}) => {
  const intl = useIntl();
  const service = musicServicesByMusicAPIID[integrationType];
  const name = service?.name ?? integrationType;
  // The dot takes the brand colour of the service: data, not a theme colour.
  const dotStyle = useMemo(() => ({ background: service?.color.primary }), [service]);
  const toggle = useCallback(() => onToggle(integrationType), [integrationType, onToggle]);

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={name}
      className={styles.row}
      disabled={disabled}
      onClick={toggle}
    >
      <span className={clsx(styles.check, checked && styles.checkOn)}>
        {checked && <FontAwesomeIcon icon={faCheck} />}
      </span>
      <span className={styles.dot} style={dotStyle} />
      <span className={styles.name}>
        {name}
        {isEmailOnlyService(integrationType) && (
          <small className={styles.note}>{intl.formatMessage(presaveSubscribeMessages.emailOnly)}</small>
        )}
      </span>
    </button>
  );
};

export const PresaveSubscribeServiceRow = TypedMemo(PresaveSubscribeServiceRowComponent);
